import {
  MagazineSpecialState,
  MagazineSpecialContentResponse,
  MagazineSpecialContent,
  MagazineSpecialCategoryResponse,
  MagazineSpecialCategory,
  MagazineSpecialKeywordResponse,
  MagazineSpecialKeyword,
  LoadContentsProps,
  UseSpecial
} from '@/types/MagazineSpecial'
import { MagazineSpecialRecommendContent } from '@/types/MagazineRecommend'
import { axiosSetting } from '@/utils/axiosSetting'
import dayjs from 'dayjs'
import { inject, provide, reactive } from 'vue'
import { axiosCancelTokenProvider } from '@/utils/axiosCancelTokenProvider'

const axios = axiosSetting('cms', 'block')

const parseCategories = (
  response: MagazineSpecialCategoryResponse[]
): MagazineSpecialCategory[] => {
  return response.map((cat) => {
    let path = ''
    if (cat.parent > 0) {
      const parentCategory = response.find((c) => c.id === cat.parent)
      path = `/magazines/${parentCategory?.slug}/category/${cat.id}`
    }
    return {
      id: cat.id,
      count: cat.count ?? 0,
      path: path,
      spThumbUrl: cat.acf.sp_cat_thumbnail.url,
      spThumbAlt: cat.acf.sp_cat_thumbnail.alt
        ? cat.acf.sp_cat_thumbnail.alt.trim()
        : '',
      pcThumbUrl: cat.acf.pc_cat_thumbnail.url,
      pcThumbAlt: cat.acf.pc_cat_thumbnail.alt
        ? cat.acf.pc_cat_thumbnail.alt.trim()
        : '',
      name: cat.name?.trim() ?? '',
      description: cat.description?.trim() ?? '',
      parent: cat.parent ? cat.parent : 0,
      slug: cat.slug ?? '',
      catSeries: cat.acf.sp_cat_series ?? ''
    }
  })
}

const parseContent = (
  response: MagazineSpecialContentResponse,
  allCategories: MagazineSpecialCategory[]
): MagazineSpecialContent => {
  const categories = allCategories.filter((cat) =>
    response.special_contents_category.includes(cat.id)
  )
  let parentCategory: MagazineSpecialCategory | undefined
  categories.forEach((cat) => {
    if (cat.parent !== 0) {
      parentCategory = allCategories.find((c) => c.id == cat.parent)
    }
  })
  const childCategories = categories.filter((cat) => cat.parent !== 0)
  const parentCategoryName = parentCategory?.name ?? ''
  const parentCategorySlug = parentCategory?.slug ?? ''
  return {
    id: response.id,
    title: response.title.rendered,
    description: response.description ?? '',
    date: dayjs(response.date).format('YYYY年M月D日'),
    content: response.content.rendered,
    categories,
    childCategories,
    parentCategorySlug,
    parentCategoryName,
    keywords: response.special_contents_keyword,
    image:
      response.thumbnail?.url ??
      (require('@/assets/images/common/noimg.jpg') as string),
    imageAlt: response.thumbnail?.alt ?? '',
    membershipOnly: response.acf.members_only_flag,
    recommendFlags: response.acf.recommend_flags,
    url: response.acf.url,
    path: `/magazines/special/detail/${response.id}`,
    isPr: response.acf.is_pr
  }
}

const parseKeywords = (
  response: MagazineSpecialKeywordResponse[]
): MagazineSpecialKeyword[] => {
  return response.map((res) => ({
    id: res.id,
    count: res.count,
    path: `/magazines/keyword/${res.name}`,
    name: res.name,
    description: res.description,
    slug: res.slug
  }))
}

export const createSpecial = () => {
  const state = reactive<MagazineSpecialState>({
    contents: [],
    totalPages: 0,
    content: {
      id: 0,
      title: '',
      description: '',
      date: '',
      content: '',
      categories: [],
      childCategories: [],
      parentCategorySlug: '',
      parentCategoryName: '',
      keywords: [],
      image: '',
      imageAlt: '',
      membershipOnly: false,
      recommendFlags: [],
      url: '',
      path: '',
      isPr: false
    },
    categories: [],
    specialCategories: [],
    seriesCategories: [],
    allChildCategories: [], // 親カテゴリを含まないすべてのカテゴリ（キーワード一覧などで仕様）
    keywords: []
  })

  const loadCategory = async (perPage = 100): Promise<void> => {
    if (state.categories.length > 0) return
    const { data } = await axios.get('/special_contents_category', {
      params: { per_page: perPage }
    })
    const response: MagazineSpecialCategoryResponse[] = data
    const categories = parseCategories(response)
    const seriesParentCategory = categories.find(
      (cat) => cat.parent === 0 && cat.slug === 'series'
    )
    const specialParentCategory = categories.find(
      (cat) => cat.parent === 0 && cat.slug === 'special'
    )
    const seriesCategories = categories.filter(
      (cat) => cat.parent === seriesParentCategory?.id
    )
    const specialCategories = categories.filter(
      (cat) => cat.parent === specialParentCategory?.id
    )
    const allChildCategories = categories.filter((cat) => cat.parent !== 0)
    state.categories = categories
    console.log('categories', categories)
    state.seriesCategories = seriesCategories
    state.specialCategories = specialCategories
    state.allChildCategories = allChildCategories
  }

  const loadContents = axiosCancelTokenProvider((cancelSource, CancelToken) => {
    return async ({
      page = 1,
      perPage,
      category,
      keyword,
      parentCategory,
      orderBy
    }: LoadContentsProps = {}): Promise<void> => {
      if (cancelSource) {
        cancelSource.cancel('load cancelled.')
      }
      cancelSource = CancelToken.source()
      const { data, headers } = await axios.get('special_contents', {
        params: {
          per_page: perPage,
          special_contents_category: category,
          keyword: keyword,
          page,
          parent_category: parentCategory,
          orderby: orderBy
        },
        cancelToken: cancelSource?.token
      })
      const response: MagazineSpecialContentResponse[] = data
      if (state.categories.length === 0) await loadCategory()
      const contents = response.map((res) =>
        parseContent(res, state.categories)
      )
      console.log('getContents', contents)
      state.contents = contents
      state.totalPages = Number(headers['x-wp-totalpages'])
    }
  })

  const loadContent = async (id: number): Promise<void> => {
    const content = state.contents.find((d) => d.id === id)
    if (content) {
      state.content = content
      console.log('content', state.content)
      return
    }
    const { data } = await axios.get(`/special_contents/${id}`)
    if (state.categories.length === 0) await loadCategory()
    state.content = parseContent(data, state.categories)
    console.log('content', state.content)
  }

  let isKeywordLoading = false // キーワードロード中は再ロードさせない
  const loadKeywords = async (): Promise<void> => {
    if (isKeywordLoading || state.keywords.length) return
    isKeywordLoading = true
    const { data } = await axios.get('special_contents_keyword')
    isKeywordLoading = false
    const response: MagazineSpecialKeywordResponse[] = data
    const keywords = parseKeywords(response)
    console.log('keywords', keywords)
    state.keywords = keywords
  }

  // getters

  const getSeriesSpecialContents = (): MagazineSpecialRecommendContent[] => {
    // ~~連載コンテンツで「レコメンド重みづけ特別加算フラグ」が指定されていない3件~~
    // 連載コンテンツがない場合もあるので、ここでは件数指定はしない。最終的に何件表示するかはView側で決める
    return state.contents
      .filter((content) => content.recommendFlags.length === 0)
      .map(
        (content): MagazineSpecialRecommendContent => {
          return {
            id: content.id.toString(),
            title: content.title,
            description: content.description,
            category: content.childCategories[0]?.name ?? '連載',
            parentCategory: content.parentCategorySlug,
            imageUrl: content.image,
            path: content.path,
            membershipOnly: content.membershipOnly,
            externalLinkUrl: content.url,
            isPr: content.isPr
          }
        }
      )
  }

  return {
    state,
    loadContents,
    loadContent,
    loadCategory,
    loadKeywords,
    getSeriesSpecialContents
  }
}

export const key = Symbol()
export const provideSpecial = () => provide(key, createSpecial())
export const useSpecial = () => inject(key) as UseSpecial
